/**
 * Shared GST Utility Functions
 * CloudSetu is registered in Rajasthan (state code 08).
 * - Intra-state (Rajasthan): CGST + SGST (each = 9% for 18% GST)
 * - Inter-state (any other state or unknown): IGST (18%)
 */

const RAJASTHAN_VARIANTS = new Set(['rajasthan', 'raj', 'rj', '08']);

/**
 * Determine GST type from GSTIN and/or state string.
 * @param {string} gstin - Client/partner GSTIN
 * @param {string} state - Client/partner state name
 * @returns {'CGST+SGST' | 'IGST'}
 */
export function determineGstType(gstin = '', state = '') {
  const gstin2 = (gstin || '').substring(0, 2).trim();
  const stateL = (state || '').trim().toLowerCase();
  const isRajasthan = gstin2 === '08' || RAJASTHAN_VARIANTS.has(stateL);
  return isRajasthan ? 'CGST+SGST' : 'IGST';
}

/**
 * Get entity state with proper priority.
 * Priority: billing_state → state_name → state → ''
 */
export function getEntityState(entity) {
  if (!entity) return '';
  return (entity.billing_state || entity.state_name || entity.state || '').trim();
}

/**
 * Get entity GSTIN with proper priority.
 * Priority: gst_number → gstin → ''
 */
export function getEntityGstin(entity) {
  if (!entity) return '';
  return (entity.gst_number || entity.gstin || '').trim();
}

/**
 * Compute full GST breakdown from base amount, gstin and state.
 * @returns {{ gst_type, subtotal, gst_amount, cgst, sgst, igst, total }}
 */
export function computeGst(baseAmount, gstin = '', state = '') {
  const amount = parseFloat(baseAmount) || 0;
  const gst_amount = Math.round(amount * 0.18 * 100) / 100;
  const gst_type = determineGstType(gstin, state);
  const half = Math.round(gst_amount / 2 * 100) / 100;
  return {
    gst_type,
    subtotal: amount,
    gst_amount,
    cgst: gst_type === 'CGST+SGST' ? half : 0,
    sgst: gst_type === 'CGST+SGST' ? half : 0,
    igst: gst_type === 'IGST' ? gst_amount : 0,
    total: Math.round((amount + gst_amount) * 100) / 100,
  };
}

/**
 * Render GST breakdown lines as an array of { label, amount } objects.
 * Use this to render CGST+SGST vs IGST in invoice/quotation UI.
 */
export function gstBreakdownLines(gstType, gstAmount, gstRate = 18) {
  const half = Math.round(gstAmount / 2 * 100) / 100;
  const halfRate = gstRate / 2;
  if (gstType === 'CGST+SGST') {
    return [
      { label: `CGST (${halfRate.toFixed(2)}%)`, amount: half },
      { label: `SGST (${halfRate.toFixed(2)}%)`, amount: half },
    ];
  }
  return [
    { label: `IGST (${gstRate.toFixed ? gstRate.toFixed(2) : gstRate}%)`, amount: gstAmount },
  ];
}
